import * as React from 'react';
import { Link } from 'gatsby';
import ClientOnly from './ClientOnly';
import {
  scrollToEmailForm,
  createScrollToEmailHandler,
} from '../utils/scrollToEmail';

const DropdownMenu = ({ label, items }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [position, setPosition] = React.useState('left');
  const dropdownRef = React.useRef(null);
  const buttonRef = React.useRef(null);
  const closeTimeout = React.useRef();

  const updatePosition = React.useCallback(() => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const dropdownWidth = 224; // w-56 = 14rem = 224px

      // If the dropdown would overflow the right edge, align it to the right
      if (buttonRect.left + dropdownWidth > viewportWidth) {
        setPosition('right');
      } else {
        setPosition('left');
      }
    }
  }, []);

  React.useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [updatePosition]);

  // Hover logic with delay
  const handleMouseEnter = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeout.current = setTimeout(() => {
      setIsOpen(false);
    }, 120); // 120ms delay
  };

  const handleKeyDown = e => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      buttonRef.current?.focus();
    }
  };

  // Add click handler for "Attend" button
  const handleAttendClick = () => {
    if (label === 'Attend') {
      scrollToEmailForm(false, null, 'navigation_attend_dropdown'); // Desktop usage
    } else {
      setIsOpen(!isOpen);
    }
  };

  return (
    <nav
      className="relative hidden md:block"
      ref={dropdownRef}
      aria-label={`${label} menu`}
    >
      <button
        ref={buttonRef}
        id={`${label.toLowerCase()}-button`}
        type="button"
        className={`flex items-center px-3 py-2 text-base font-semibold transition-colors duration-150
          text-gray-700 hover:text-gray-900 font-optima
          ${isOpen ? 'bg-gray-100 text-gray-900 rounded-t' : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={`${label.toLowerCase()}-menu`}
        onClick={handleAttendClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {label}
      </button>

      {isOpen && (
        <div
          id={`${label.toLowerCase()}-menu`}
          className={`absolute mt-2 w-56 rounded-b-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 border-t-4 border-gray-900
            ${position === 'right' ? 'right-0' : 'left-0'}`}
          role="menu"
          aria-orientation="vertical"
          aria-labelledby={`${label.toLowerCase()}-button`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          <div className="py-2">
            {items.map((item, index) => (
              <Link
                key={index}
                to={item.path}
                className="block px-4 py-2 text-base text-gray-700 hover:bg-gray-50 hover:text-gray-900 focus:bg-gray-50 focus:text-gray-900 focus:outline-none"
                role="menuitem"
                tabIndex={0}
                {...(item.external
                  ? { target: '_blank', rel: 'noopener noreferrer' }
                  : {})}
                onClick={item.onClick}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

const NavigationContent = () => {
  const [pathname, setPathname] = React.useState('/');
  const [mobileOpen, setMobileOpen] = React.useState(false);

  React.useEffect(() => {
    setPathname(window.location.pathname);
    const handleRouteChange = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);

  const isCurrentPage = React.useCallback(
    path => pathname === path,
    [pathname]
  );

  const menuItems = {
    attend: [
      {
        label: 'Live events',
        path: '/',
        onClick: createScrollToEmailHandler(
          false,
          () => setMobileOpen(false),
          'navigation_live_events'
        ),
      },
      { label: 'Private events', path: '/private-events' },
      { label: 'Pick your song', path: '/songs' },
    ],
  };

  // Prevent scrolling when mobile menu is open
  React.useEffect(() => {
    if (mobileOpen) {
      // Store original overflow value and position
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const scrollY = window.scrollY;

      // Instead of hiding overflow completely, use a more mobile-friendly approach
      if (window.innerWidth <= 768) {
        // Only apply to mobile screens
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
      } else {
        document.body.style.overflow = 'hidden';
      }

      // Return cleanup function
      return () => {
        if (window.innerWidth <= 768) {
          document.body.style.position = originalPosition || '';
          document.body.style.top = '';
          document.body.style.width = '';
          window.scrollTo(0, scrollY);
        } else {
          document.body.style.overflow = originalOverflow || '';
        }
      };
    } else {
      // Ensure body scroll is restored when menu closes
      if (window.innerWidth <= 768) {
        const scrollY = parseInt(document.body.style.top || '0') * -1;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        if (scrollY) window.scrollTo(0, scrollY);
      } else {
        document.body.style.overflow = '';
      }
    }
  }, [mobileOpen]);

  // Additional cleanup on unmount
  React.useEffect(() => {
    return () => {
      // More gentle cleanup
      if (document.body.style.position === 'fixed') {
        const scrollY = parseInt(document.body.style.top || '0') * -1;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        if (scrollY) window.scrollTo(0, scrollY);
      } else {
        document.body.style.overflow = '';
      }
    };
  }, []);

  return (
    <nav
      className="z-50 bg-white border-b border-soft-gray shadow-sm"
      aria-label="Main navigation"
    >
      <div className="max-w-8xl mx-auto px-4 sm:px-24 lg:px-40">
        <div className="flex items-center justify-between h-16">
          {/* Left: Logo */}
          <div className="flex-shrink-0">
            <Link
              to="/"
              className="font-libre text-gray-800 text-lg sm:text-xl md:text-2xl font-bold hover:text-gray-600 transition-all"
              aria-current={isCurrentPage('/') ? 'page' : undefined}
            >
              Sing with the Stars
            </Link>
          </div>

          {/* Right: Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <DropdownMenu label="Attend" items={menuItems.attend} />
            <a
              href="https://blog.singwiththestars.com"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 text-base font-semibold text-gray-700 hover:text-gray-900 transition-colors duration-150 font-optima"
            >
              Discover
            </a>
          </div>

          {/* Hamburger for Mobile */}
          <button
            className="md:hidden flex items-center justify-center p-2 text-gray-700 hover:text-gray-900 focus:outline-none"
            aria-label="Open menu"
            onClick={() => setMobileOpen(true)}
          >
            <svg
              className="h-7 w-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <button
        className={`fixed inset-0 z-50 transition-all duration-300 ${mobileOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
        style={{ background: mobileOpen ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0)' }}
        aria-hidden={!mobileOpen}
        onClick={() => setMobileOpen(false)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape')
            setMobileOpen(false);
        }}
        aria-label="Close menu overlay"
      />
      {/* Mobile Menu Drawer - now full width */}
      <div
        className={`fixed top-0 right-0 h-full w-full bg-white shadow-xl z-50 border-l border-soft-gray transform transition-transform duration-300 ease-in-out overflow-y-auto
          ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
      >
        <div className="flex justify-end p-4">
          <button
            className="p-3 text-gray-700 hover:text-gray-900 focus:outline-none rounded-full hover:bg-gray-100 transition-colors duration-200"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
          >
            <svg
              className="h-7 w-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <nav className="flex flex-col gap-6 px-8 pb-8">
          <div>
            <button
              className="text-lg font-bold text-gray-900 mb-2 text-left w-full cursor-pointer"
              onClick={createScrollToEmailHandler(
                true,
                () => setMobileOpen(false),
                'navigation_mobile_attend'
              )}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  scrollToEmailForm(
                    true,
                    () => setMobileOpen(false),
                    'navigation_mobile_attend'
                  );
                }
              }}
            >
              Attend
            </button>
            {menuItems.attend.map((item, idx) => (
              <Link
                key={idx}
                to={item.path}
                className="block py-2 text-base text-gray-700 hover:text-gray-900"
                {...(item.external
                  ? { target: '_blank', rel: 'noopener noreferrer' }
                  : {})}
                onClick={e => {
                  if (item.onClick) {
                    // For Live events, use the mobile scroll handler
                    if (item.label === 'Live events') {
                      createScrollToEmailHandler(
                        true,
                        () => setMobileOpen(false),
                        'navigation_mobile_live_events'
                      )(e);
                    } else {
                      item.onClick(e);
                      setMobileOpen(false);
                    }
                  } else {
                    setMobileOpen(false);
                  }
                }}
              >
                {item.label}
              </Link>
            ))}
          </div>
          <div>
            <a
              href="https://blog.singwiththestars.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block py-2 text-base text-gray-700 hover:text-gray-900"
            >
              Discover
            </a>
          </div>
        </nav>
      </div>
    </nav>
  );
};

// Navigation skeleton to prevent layout shift during hydration
const NavigationSkeleton = () => (
  <nav
    className="z-50 bg-white border-b border-soft-gray shadow-sm"
    aria-label="Navigation loading"
  >
    <div className="max-w-8xl mx-auto px-4 sm:px-24 lg:px-40">
      <div className="flex items-center justify-between h-16">
        {/* Logo skeleton */}
        <div className="flex-shrink-0">
          <div className="font-libre text-gray-800 text-lg sm:text-xl md:text-2xl font-bold">
            Sing with the Stars
          </div>
        </div>
        {/* Desktop nav skeleton */}
        <div className="hidden md:flex items-center space-x-6">
          <div className="px-3 py-2 text-base font-semibold text-gray-700 font-optima">
            Attend
          </div>
          <div className="px-3 py-2 text-base font-semibold text-gray-700 font-optima">
            Discover
          </div>
        </div>
        {/* Mobile hamburger skeleton */}
        <div className="md:hidden flex items-center justify-center p-2">
          <svg
            className="h-7 w-7 text-gray-700"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </div>
      </div>
    </div>
  </nav>
);

const Navigation = () => {
  return (
    <ClientOnly fallback={<NavigationSkeleton />}>
      <NavigationContent />
    </ClientOnly>
  );
};

export default Navigation;
